import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { MessageWithDetails } from '@/types/messaging';
import {
  type MessagingState,
  type ScheduledMessage,
  evictStaleConversations,
  MAX_CACHED_CONVERSATIONS,
  MAX_MESSAGES_PER_CONVERSATION,
} from './messaging.store.types';

export type { ScheduledMessage };
export { MAX_CACHED_CONVERSATIONS, MAX_MESSAGES_PER_CONVERSATION };

export const useMessagingStore = create<MessagingState>()(
  persist(
    (set, get) => ({
      // Initial state
      conversations: [],
      activeConversationId: null,
      messagesByConversation: {} as Record<string, MessageWithDetails[]>,
      typingUsers: {},
      unreadCounts: {},
      totalUnread: 0,
      isConnected: false,
      onlineUsers: new Set(),
      isPanelOpen: false,
      isNewConversationModalOpen: false,
      replyingTo: {},
      drafts: {},
      pinnedMessages: {},
      searchQuery: '',
      searchResults: [],
      isSearching: false,
      conversationFilter: 'all',
      conversationSearch: '',
      mutedConversations: new Set(),
      scheduledMessages: [],

      // Conversations
      setConversations: (conversations) =>
        set({
          conversations,
          unreadCounts: conversations.reduce(
            (acc, c) => ({ ...acc, [c.id]: c.unread_count || 0 }),
            {}
          ),
          totalUnread: conversations.reduce((sum, c) => sum + (c.unread_count || 0), 0),
        }),

      addConversation: (conversation) =>
        set((state) => ({
          conversations: [conversation, ...state.conversations.filter((c) => c.id !== conversation.id)],
        })),

      updateConversation: (conversationId, updates) =>
        set((state) => ({
          conversations: state.conversations.map((c) =>
            c.id === conversationId ? { ...c, ...updates } : c
          ),
        })),

      removeConversation: (conversationId) =>
        set((state) => ({
          conversations: state.conversations.filter((c) => c.id !== conversationId),
          activeConversationId:
            state.activeConversationId === conversationId ? null : state.activeConversationId,
        })),

      setActiveConversation: (conversationId) =>
        set({ activeConversationId: conversationId }),

      // Messages
      setMessages: (conversationId, messages) =>
        set((state) => {
          const capped = messages.length > MAX_MESSAGES_PER_CONVERSATION
            ? messages.slice(-MAX_MESSAGES_PER_CONVERSATION)
            : messages;
          const updated = { ...state.messagesByConversation, [conversationId]: capped };
          const protect = new Set([conversationId, state.activeConversationId].filter(Boolean) as string[]);
          return { messagesByConversation: evictStaleConversations(updated, protect, MAX_CACHED_CONVERSATIONS) };
        }),

      addMessage: (conversationId, message) =>
        set((state) => {
          const existing = state.messagesByConversation[conversationId] || [];
          if (existing.some((m) => m.id === message.id)) return state;
          const appended = [...existing, message];
          const capped = appended.length > MAX_MESSAGES_PER_CONVERSATION
            ? appended.slice(-MAX_MESSAGES_PER_CONVERSATION)
            : appended;
          const updated = { ...state.messagesByConversation, [conversationId]: capped };
          const protect = new Set([conversationId, state.activeConversationId].filter(Boolean) as string[]);
          return { messagesByConversation: evictStaleConversations(updated, protect, MAX_CACHED_CONVERSATIONS) };
        }),

      updateMessage: (conversationId, messageId, updates) =>
        set((state) => ({
          messagesByConversation: {
            ...state.messagesByConversation,
            [conversationId]: (state.messagesByConversation[conversationId] || []).map((m) =>
              m.id === messageId ? { ...m, ...updates } : m
            ),
          },
        })),

      removeMessage: (conversationId, messageId) =>
        set((state) => ({
          messagesByConversation: {
            ...state.messagesByConversation,
            [conversationId]: (state.messagesByConversation[conversationId] || []).filter(
              (m) => m.id !== messageId
            ),
          },
        })),

      prependMessages: (conversationId, messages) =>
        set((state) => {
          const existing = state.messagesByConversation[conversationId] || [];
          const existingIds = new Set(existing.map((m) => m.id));
          const newMessages = messages.filter((m) => !existingIds.has(m.id));
          const merged = [...newMessages, ...existing];
          const capped = merged.length > MAX_MESSAGES_PER_CONVERSATION
            ? merged.slice(-MAX_MESSAGES_PER_CONVERSATION)
            : merged;
          const updated = { ...state.messagesByConversation, [conversationId]: capped };
          const protect = new Set([conversationId, state.activeConversationId].filter(Boolean) as string[]);
          return { messagesByConversation: evictStaleConversations(updated, protect, MAX_CACHED_CONVERSATIONS) };
        }),

      // Typing
      setTypingUsers: (conversationId, users) =>
        set((state) => ({ typingUsers: { ...state.typingUsers, [conversationId]: users } })),

      addTypingUser: (conversationId, user) =>
        set((state) => {
          const current = state.typingUsers[conversationId] || [];
          if (current.some((u) => u.user_id === user.user_id)) return state;
          return { typingUsers: { ...state.typingUsers, [conversationId]: [...current, user] } };
        }),

      removeTypingUser: (conversationId, userId) =>
        set((state) => ({
          typingUsers: {
            ...state.typingUsers,
            [conversationId]: (state.typingUsers[conversationId] || []).filter((u) => u.user_id !== userId),
          },
        })),

      // Unread
      setUnreadCount: (conversationId, count) =>
        set((state) => {
          const newCounts = { ...state.unreadCounts, [conversationId]: count };
          return { unreadCounts: newCounts, totalUnread: Object.values(newCounts).reduce((sum, c) => sum + c, 0) };
        }),

      incrementUnreadCount: (conversationId) =>
        set((state) => {
          const newCounts = { ...state.unreadCounts, [conversationId]: (state.unreadCounts[conversationId] || 0) + 1 };
          return { unreadCounts: newCounts, totalUnread: Object.values(newCounts).reduce((sum, c) => sum + c, 0) };
        }),

      clearUnreadCount: (conversationId) =>
        set((state) => {
          const newCounts = { ...state.unreadCounts, [conversationId]: 0 };
          return { unreadCounts: newCounts, totalUnread: Object.values(newCounts).reduce((sum, c) => sum + c, 0) };
        }),

      setTotalUnread: (count) => set({ totalUnread: count }),

      // Connection
      setConnected: (connected) => set({ isConnected: connected }),

      // Online users
      setOnlineUsers: (userIds) => set({ onlineUsers: new Set(userIds) }),
      addOnlineUser: (userId) =>
        set((state) => ({ onlineUsers: new Set([...state.onlineUsers, userId]) })),
      removeOnlineUser: (userId) =>
        set((state) => {
          const newSet = new Set(state.onlineUsers);
          newSet.delete(userId);
          return { onlineUsers: newSet };
        }),
      isUserOnline: (userId) => get().onlineUsers.has(userId),

      // UI
      setPanelOpen: (open) => set({ isPanelOpen: open }),
      togglePanel: () => set((state) => ({ isPanelOpen: !state.isPanelOpen })),
      setNewConversationModalOpen: (open) => set({ isNewConversationModalOpen: open }),

      // Reply
      setReplyingTo: (conversationId, message) =>
        set((state) => ({ replyingTo: { ...state.replyingTo, [conversationId]: message } })),
      clearReplyingTo: (conversationId) =>
        set((state) => ({ replyingTo: { ...state.replyingTo, [conversationId]: null } })),

      // Drafts
      setDraft: (conversationId, content) =>
        set((state) => ({ drafts: { ...state.drafts, [conversationId]: content } })),
      clearDraft: (conversationId) =>
        set((state) => {
          const { [conversationId]: _, ...rest } = state.drafts;
          return { drafts: rest };
        }),
      getDraft: (conversationId) => get().drafts[conversationId] || '',

      // Pinned messages
      setPinnedMessages: (conversationId, messages) =>
        set((state) => ({ pinnedMessages: { ...state.pinnedMessages, [conversationId]: messages } })),
      addPinnedMessage: (conversationId, message) =>
        set((state) => ({
          pinnedMessages: {
            ...state.pinnedMessages,
            [conversationId]: [...(state.pinnedMessages[conversationId] || []), message],
          },
        })),
      removePinnedMessage: (conversationId, messageId) =>
        set((state) => ({
          pinnedMessages: {
            ...state.pinnedMessages,
            [conversationId]: (state.pinnedMessages[conversationId] || []).filter((m) => m.id !== messageId),
          },
        })),

      // Search
      setSearchQuery: (query) => set({ searchQuery: query }),
      setSearchResults: (results) => set({ searchResults: results }),
      setIsSearching: (searching) => set({ isSearching: searching }),
      clearSearch: () => set({ searchQuery: '', searchResults: [], isSearching: false }),

      // Conversation filters
      setConversationFilter: (filter) => set({ conversationFilter: filter }),
      setConversationSearch: (search) => set({ conversationSearch: search }),

      // Muted conversations
      muteConversation: (conversationId) =>
        set((state) => ({ mutedConversations: new Set([...state.mutedConversations, conversationId]) })),
      unmuteConversation: (conversationId) =>
        set((state) => {
          const newSet = new Set(state.mutedConversations);
          newSet.delete(conversationId);
          return { mutedConversations: newSet };
        }),
      toggleMuteConversation: (conversationId) =>
        set((state) => {
          const newSet = new Set(state.mutedConversations);
          if (newSet.has(conversationId)) { newSet.delete(conversationId); } else { newSet.add(conversationId); }
          return { mutedConversations: newSet };
        }),
      isConversationMuted: (conversationId) => get().mutedConversations.has(conversationId),

      // Scheduled messages
      addScheduledMessage: (message) =>
        set((state) => ({
          scheduledMessages: [
            ...state.scheduledMessages,
            {
              ...message,
              id: `scheduled-${Date.now()}-${Math.random().toString(36).slice(2)}`,
              createdAt: new Date().toISOString(),
            },
          ],
        })),
      removeScheduledMessage: (id) =>
        set((state) => ({ scheduledMessages: state.scheduledMessages.filter((m) => m.id !== id) })),
      getScheduledMessages: (conversationId) =>
        get().scheduledMessages.filter((m) => m.conversationId === conversationId),

      // Clear
      clearMessages: (conversationId) =>
        set((state) => {
          const { [conversationId]: _m, ...restMessages } = state.messagesByConversation;
          const { [conversationId]: _t, ...restTyping } = state.typingUsers;
          const { [conversationId]: _r, ...restReply } = state.replyingTo;
          const { [conversationId]: _p, ...restPinned } = state.pinnedMessages;
          return { messagesByConversation: restMessages, typingUsers: restTyping, replyingTo: restReply, pinnedMessages: restPinned };
        }),

      clearAllState: () => {
        // Purge the persisted copy too — mutedConversations / scheduledMessages
        // are written to the raw 'messaging-storage' localStorage key, which
        // would otherwise survive logout / tenant switch and leak across users.
        try {
          if (typeof localStorage !== 'undefined') {
            localStorage.removeItem('messaging-storage');
          }
        } catch {
          // Non-fatal (SSR / storage disabled).
        }
        set({
          conversations: [],
          activeConversationId: null,
          messagesByConversation: {},
          typingUsers: {},
          unreadCounts: {},
          totalUnread: 0,
          isConnected: false,
          onlineUsers: new Set(),
          isPanelOpen: false,
          isNewConversationModalOpen: false,
          replyingTo: {},
          drafts: {},
          pinnedMessages: {},
          searchQuery: '',
          searchResults: [],
          isSearching: false,
          conversationFilter: 'all',
          conversationSearch: '',
          mutedConversations: new Set(),
          scheduledMessages: [],
        });
      },
    }),
    {
      name: 'messaging-storage',
      partialize: (state): Partial<MessagingState> => ({
        mutedConversations: state.mutedConversations,
        scheduledMessages: state.scheduledMessages,
      }),
      storage: {
        getItem: (name) => {
          const str = localStorage.getItem(name);
          if (!str) return null;
          try {
            const parsed = JSON.parse(str);
            if (parsed.state?.mutedConversations) {
              parsed.state.mutedConversations = new Set(parsed.state.mutedConversations);
            }
            return parsed;
          } catch {
            localStorage.removeItem(name);
            return null;
          }
        },
        setItem: (name, value) => {
          const toStore = {
            ...value,
            state: {
              ...value.state,
              mutedConversations: value.state?.mutedConversations
                ? [...value.state.mutedConversations]
                : [],
            },
          };
          localStorage.setItem(name, JSON.stringify(toStore));
        },
        removeItem: (name) => localStorage.removeItem(name),
      },
    }
  )
);
